/**
 * Opens the confirmation popup after a form posts.
 *
 * Shopify answers a successful post with a redirect back to the page, so the
 * popup is rendered server-side and simply opened here.
 *
 * Several forms on one page can share a success parameter — every
 * {% form 'customer' %} on the page sees customer_posted=true, including the
 * footer newsletter. So each submit records which form it came from, and only
 * that form's popup opens after the reload.
 */

/**
 * The snippet renders this script once per form, so a page can run it twice.
 * The IIFE keeps the declarations off the shared global scope; the guards below
 * handle the work that must happen only once.
 */
(function () {
  const SUBMITTED_KEY = 'dm:form-popup-submitted';

  function popupForForm(form) {
    if (!form || !form.id) return null;
    return (
      Array.from(document.querySelectorAll('[data-form-popup-form]')).find(
        (popup) => popup.dataset.formPopupForm === form.id
      ) || null
    );
  }

  /**
   * A page can hold more than one popup, and each renders this script, so set up
   * once. The submitted key is read and cleared here, so it opens the popup for
   * this reload only and a later refresh does not bring it back.
   */
  if (!('dmFormPopupSubmitted' in window)) {
    try {
      window.dmFormPopupSubmitted = sessionStorage.getItem(SUBMITTED_KEY);
      sessionStorage.removeItem(SUBMITTED_KEY);
    } catch (_) {
      // Private browsing or blocked storage: fall back to the server-rendered state.
      window.dmFormPopupSubmitted = null;
    }

    // Capture phase, so the key is stored even if a handler stops the event later.
    document.addEventListener(
      'submit',
      (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement)) return;

        // Forms without a popup — the footer newsletter, say — still register, so
        // their reload cannot open somebody else's popup.
        const key = popupForForm(form)?.dataset.formPopupKey || 'other';

        try {
          sessionStorage.setItem(SUBMITTED_KEY, key);
        } catch (_) {
          // Nothing to do; the server-rendered state still applies.
        }
      },
      true
    );
  }

  class FormSuccessPopup extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-form-popup-dialog]');
      this.onKeydown = this.onKeydown.bind(this);

      this.querySelectorAll('[data-form-popup-close]').forEach((control) =>
        control.addEventListener('click', (event) => {
          event.preventDefault();
          this.close();
        })
      );

      this.querySelector('[data-form-popup-overlay]')?.addEventListener('click', () => this.close());

      // The section renders it open when its own form posted successfully.
      if (this.hidden) return;

      const submitted = window.dmFormPopupSubmitted;
      if (submitted && submitted !== this.dataset.formPopupKey) {
        // Another form on the page was the one submitted.
        this.hidden = true;
        return;
      }

      this.open();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKeydown);
      document.documentElement.classList.remove('form-popup-open');
    }

    open() {
      this.hidden = false;
      this.returnFocusTo = document.activeElement;
      document.documentElement.classList.add('form-popup-open');
      document.addEventListener('keydown', this.onKeydown);
      this.dialog?.focus();
      this.cleanUrl();
    }

    close() {
      this.hidden = true;
      document.documentElement.classList.remove('form-popup-open');
      document.removeEventListener('keydown', this.onKeydown);
      if (this.returnFocusTo instanceof HTMLElement) this.returnFocusTo.focus();
    }

    onKeydown(event) {
      if (event.key === 'Escape') this.close();
    }

    /** Drops the success parameter, so a refresh does not re-open the popup. */
    cleanUrl() {
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete('contact_posted');
        url.searchParams.delete('customer_posted');
        url.hash = '';
        window.history.replaceState({}, '', url.toString());
      } catch (_) {
        // URL API restricted; the popup still works.
      }
    }
  }

  if (!customElements.get('form-success-popup')) {
    customElements.define('form-success-popup', FormSuccessPopup);
  }
})();
